import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "yn-cookie-consent-v1";

// Simple EU/Ads-compliant cookie banner. Persists a "granted" or "denied"
// decision in localStorage. When granted, sets GA4 consent to granted;
// when denied, ensures GA4 consent remains denied.
export default function CookieBanner() {
  const [decision, setDecision] = useState(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "granted" || stored === "denied") {
        setDecision(stored);
        applyConsent(stored);
      }
    } catch (_) {
      // localStorage disabled — banner will still show
    }
  }, []);

  function applyConsent(value) {
    if (typeof window === "undefined" || !window.gtag) return;
    window.gtag("consent", "update", {
      ad_storage: value === "granted" ? "granted" : "denied",
      ad_user_data: value === "granted" ? "granted" : "denied",
      ad_personalization: value === "granted" ? "granted" : "denied",
      analytics_storage: value === "granted" ? "granted" : "denied",
    });
  }

  function choose(value) {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (_) {}
    setDecision(value);
    applyConsent(value);
  }

  if (decision) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-50 rounded-2xl border border-[color:var(--yn-border)] bg-white shadow-xl p-5 sm:p-6"
    >
      <div className="flex items-start gap-3">
        <Cookie
          size={22}
          className="mt-1 shrink-0"
          style={{ color: "var(--yn-primary)" }}
          aria-hidden="true"
        />
        <div className="flex-1">
          <p className="font-heading text-base font-semibold text-[color:var(--yn-ink)]">
            We use cookies
          </p>
          <p className="mt-2 text-sm leading-relaxed text-[color:var(--yn-muted)]">
            YeboNadi uses cookies to measure how visitors reach the site and to
            improve our advertising. You can decline without losing any part
            of the site. Read our{" "}
            <Link to="/privacy" className="underline text-[color:var(--yn-primary)]">
              Privacy Policy
            </Link>.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => choose("granted")}
              data-testid="cookie-accept-btn"
              className="rounded-full px-4 py-2 text-sm font-medium text-white transition-colors"
              style={{ background: "var(--yn-primary)" }}
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={() => choose("denied")}
              data-testid="cookie-decline-btn"
              className="rounded-full px-4 py-2 text-sm font-medium border border-[color:var(--yn-border)] text-[color:var(--yn-ink)] hover:bg-[color:var(--yn-border)]/40 transition-colors"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
